import { Box, Tab, Tabs } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';
import { logMessage } from '../../logger';
import { toTitleCase } from '../../modules/string';
import { fromNowSeconds, fromNowSecondsZone } from '../../modules/renderDate';
import moment from 'moment/moment';
import { InnerStyledList, StyledListItem, StyledListItemText, Title } from './style';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

interface ObjectTabsProps {
  object: any;
}

const ObjectTabs: React.FC<ObjectTabsProps> = ({ object: store }) => {
  const intl = useIntl();
  const [value, setValue] = useState(0);
  const storeProvider = store?.basic.provider;

  const handleChange = (event, newValue) => {
    logMessage('event', event);
    setValue(newValue);
  };

  const a11yProps = (index: number) => ({
    id: `vertical-tab-${index}`,
    'aria-controls': `vertical-tabpanel-${index}`,
  });

  const TabPanel = ({ children, value, index, ...other }: TabPanelProps) => {
    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && (
          <Box>
            <div>{children}</div>
          </Box>
        )}
      </div>
    );
  };

  const fieldPreProcess = (field) => {
    if (field === 'ownerId') {
      return null;
    }
    return toTitleCase(field);
  };

  const ObjectFieldValue = ({ object: store, parent = '' }) => {
    const fieldValuePreProcess = (field, value) => {
      if (['ownerId', 'owner_id'].find((exclude) => exclude === field)) {
        return null;
      }

      if (String(field).toLowerCase().endsWith('time') && !isNaN(value)) {
        return (
          fromNowSecondsZone(value) +
          ', ' +
          moment(value * 1000)
            .toDate()
            .toISOString()
            .replace('T', ' ')
        );
      }

      if (String(field).toLowerCase().endsWith('expires') && !isNaN(value)) {
        const formatExpiration = (timestamp: number, relativeTime: string) => {
          return `(${value}) ${relativeTime}, ${moment(timestamp)
            .toDate()
            .toISOString()
            .replace('T', ' ')}`;
        };
      
        if (storeProvider === 'tiktok') {
          return formatExpiration(
            value * 1000,
            fromNowSeconds(value)
          );
        }
        
        const futureTimestamp = Date.now() + value * 1000;
        return formatExpiration(
          futureTimestamp,
          fromNowSecondsZone(futureTimestamp / 1000)
        );
      }

      if (value?.date) {
        return value?.date;
      }

      if (value?.$oid) {
        return value?.$oid;
      }

      return value;
    };

    return (
      <>
        {Object.keys(store || {}).map((infoKey, i) => {
          return (() => {
            if (infoKey === '_id') return;
            let fieldTitle = fieldPreProcess(infoKey);
            if (fieldTitle && store?.[infoKey]) {
              const translatedFieldTitle = intl.formatMessage({
                id: 'storeDetails.tabs.tabpanel.label.' + infoKey,
                defaultMessage: fieldTitle,
              });
              return (
                <InnerStyledList key={`inner-styled-list-${i}`}>
                  <Title key={`store-field-value-${parent}-${i}`}>{translatedFieldTitle}</Title>
                  {Object.keys(store?.[infoKey] || {}).map((field, j) => (
                    <StyledListItem key={`store-field-value-${parent}-${field}-${i}-${j}`}>
                      {(() => {
                        let fieldTitle = fieldPreProcess(field);

                        let value = fieldValuePreProcess(field, store?.[infoKey]?.[field]);
                        if (value === null) {
                          return;
                        }

                        if (typeof value === 'object') {
                          return <ObjectFieldValue object={{ [field]: value }} />;
                        }

                        return (
                          <StyledListItemText>
                            <div>{fieldTitle}</div>
                            <div>{value || 'n/a'}</div>
                          </StyledListItemText>
                        );
                      })()}
                    </StyledListItem>
                  ))}
                </InnerStyledList>
              );
            }
            return;
          })();
        })}
      </>
    );
  };

  return (
    <>
      <Tabs
        variant="scrollable"
        value={value}
        onChange={handleChange}
        aria-label="Vertical tabs example"
        sx={{ borderBottom: 1, borderColor: 'divider' }}
      >
        {Object.keys(store || {}).map((infoKey, i) => {
          return (() => {
            if (infoKey === '_id') return;
            let fieldTitle = fieldPreProcess(infoKey);
            if (fieldTitle && store?.[infoKey]) {
              const translatedFieldTitle = intl.formatMessage({
                id: 'storeDetails.tabs.tab.title.' + infoKey,
                defaultMessage: fieldTitle,
              });
              return (
                <Tab
                  label={translatedFieldTitle}
                  {...a11yProps(0)}
                  key={`inner-styled-list-${i}`}
                />
              );
            }
          })();
        })}
      </Tabs>

      {Object.keys(store || {}).map((infoKey, i) => {
        return (() => {
          if (infoKey === '_id') return;
          let fieldTitle = fieldPreProcess(infoKey);
          if (fieldTitle && store?.[infoKey]) {
            return (
              <TabPanel value={value} index={i - 1} key={`inner-styled-list-${i}`}>
                <ObjectFieldValue object={{ [infoKey]: store?.[infoKey] }} />
              </TabPanel>
            );
          }

          return;
        })();
      })}
    </>
  );
};

export default ObjectTabs;
